import { DEFAULT_QUERY_OPTIONS } from "#hooks/adapters/adapterUtils";
import { useQuery } from "@tanstack/react-query";
import { DropdownInput, useFormContext } from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  AssetType,
  DeviceAdapter,
  EquipmentAdapter,
  FacilityAdapter,
  FlowAdapter,
  SortDirectionType,
  TemplatedReportInputType,
} from "@validereinc/domain";
import { get, uniqBy } from "es-toolkit/compat";
import React from "react";

export type AttributeLookupOptionType = {
  id: string;
  name: string;
};

const defaultAssetSort = {
  sortBy: "name",
  sortDirection: "asc" as SortDirectionType,
};

export const AttributeLookupInput = ({
  lookup_entity_type,
  lookup_entity_attribute,
  name,
  isMulti = false,
  ...restProps
}: Omit<TemplatedReportInputType, "lookup_entity_attribute"> & {
  name: string;
  lookup_entity_attribute: string;
  isMulti?: boolean;
}) => {
  const { watch } = useFormContext();
  const value = watch(name);

  const { data, isLoading } = useQuery({
    queryKey: [
      lookup_entity_type,
      "attributeDistinct",
      lookup_entity_attribute,
    ] as const,
    queryFn: async ({ queryKey: [assetType, _, attribute] }) => {
      let result;
      switch (assetType) {
        case AssetType.ASSET_GROUP:
          result = await AssetGroupAdapter.getList(defaultAssetSort);
          break;
        case AssetType.DEVICE:
          result = await DeviceAdapter.getList(defaultAssetSort);
          break;
        case AssetType.EQUIPMENT:
          result = await EquipmentAdapter.getList(defaultAssetSort);
          break;
        case AssetType.FACILITY:
          result = await FacilityAdapter.getList(defaultAssetSort);
          break;
        case AssetType.FLOW:
          result = await FlowAdapter.getList(defaultAssetSort);
          break;
      }

      /** The backend requires we return the UUID of the entity we want to get the attribute from */
      const attributeOptions: AttributeLookupOptionType[] = [];

      for (const item of result?.data ?? []) {
        const id = item.id;
        const name = attribute === "id" ? item.name : get(item, attribute);
        if (id && name) {
          attributeOptions.push({ id, name });
        }
      }

      return uniqBy(attributeOptions, ({ name }) => name);
    },
    ...DEFAULT_QUERY_OPTIONS,
  });

  return (
    <DropdownInput
      name={name}
      options={data ?? []}
      valueKey="id"
      labelKey="name"
      isMulti={isMulti}
      isDisabled={isLoading}
      isFluid
      defaultValue={value ?? ""}
      {...restProps}
    />
  );
};
